import { useEffect, useState } from "react";

interface UseAuthResendOptions {
  email?: string;
  type: "otp" | "magic-link";
}

interface UseAuthResendReturn {
  resendLoading: boolean;
  resendCooldown: number;
  message: string;
  error: string;
  handleResend: () => Promise<void>;
  clearMessage: () => void;
}

export function useAuthResend({
  email,
  type,
}: UseAuthResendOptions): UseAuthResendReturn {
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [resendCooldown]);

  const handleResend = async () => {
    if (!email || resendCooldown > 0) return;

    setResendLoading(true);
    setMessage("");
    setError("");

    try {
      const response = await fetch("/api/auth/resend-auth-link", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        setResendCooldown(30); // 30 second cooldown
        if (type === "otp") {
          setError("");
          setMessage("");
        } else {
          setMessage("A new sign-in link has been sent to your email.");
        }
      } else {
        const errorMessage =
          data.message ||
          `Failed to send ${type === "otp" ? "OTP" : "sign-in link"}`;
        setError(errorMessage);
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const clearMessage = () => {
    setMessage("");
    setError("");
  };

  return {
    resendLoading,
    resendCooldown,
    message,
    error,
    handleResend,
    clearMessage,
  };
}
